export function extractApiErrorMessage(error: unknown): string | null {
    if (
        error instanceof Error &&
        error.name === 'Non200ResponseError' &&
        typeof (error as Error & { detail?: unknown }).detail === 'string'
    ) {
        return (error as Error & { detail: string }).detail;
    }
    return null;
}

function errorReason(error: unknown): string | null {
    if (!(error instanceof Error) || error.name !== 'Non200ResponseError') {
        return null;
    }
    const detail = (error as Error & { detail?: unknown }).detail;
    if (typeof detail !== 'object' || detail === null) {
        return null;
    }
    const reason = (detail as { reason?: unknown }).reason;
    return typeof reason === 'string' ? reason : null;
}

function hasErrorReason(error: unknown, reason: string): boolean {
    return errorReason(error) === reason;
}

export function isNotFoundResponse(error: unknown): boolean {
    return error instanceof Error
        && error.name === 'Non200ResponseError'
        && (error as Error & { status?: unknown }).status === 404;
}

export function isRefusedByBackend(error: unknown): boolean {
    if (!(error instanceof Error) || error.name !== 'Non200ResponseError') {
        return false;
    }
    const status = (error as Error & { status?: unknown }).status;
    return typeof status === 'number' && status >= 400 && status < 500;
}

const GENERIC_ERROR_MESSAGE = 'Er is een fout opgetreden. Probeer het later opnieuw.';
const DOCUMENT_WAS_CHANGED_MESSAGE =
    'Iemand anders heeft dit document intussen gewijzigd. Herlaad het document om de nieuwste versie te zien.';

export type FixedAssetErrorReason =
    | 'BILL_IS_DELETED'
    | 'BILL_IS_BOOKED'
    | 'BILL_NOT_A_PURCHASE_INVOICE'
    | 'BILL_NOT_INVOICE_OR_RECEIPT'
    | 'BILL_IS_CREDIT_NOTE'
    | 'BILL_NOT_IN_EURO'
    | 'BILL_HAS_TRANSITORY_LINE'
    | 'BILL_HAS_NO_LINE_ITEMS'
    | 'DOCUMENT_HAS_NO_BILL'
    | 'FIXED_ASSET_ACCOUNT_NOT_ALLOWED'
    | 'INVALID_FIXED_ASSET'
    | 'FIXED_ASSET_ALREADY_LINKED'
    | 'FIXED_ASSET_NOT_LINKED'
    | 'SUBLEDGER_NOT_FOR_BUSINESS'
    | 'DOCUMENT_WAS_CHANGED'
    | 'BILL_ALREADY_HAS_FIXED_ASSET';

const FIXED_ASSET_ERROR_MESSAGES: Record<FixedAssetErrorReason, string> = {
    BILL_IS_DELETED: 'Deze factuur is verwijderd.',
    BILL_IS_BOOKED: 'Deze factuur is al geboekt en kan niet aan een vast actief worden gekoppeld.',
    BILL_NOT_A_PURCHASE_INVOICE: 'Alleen inkoopfacturen kunnen aan een vast actief worden gekoppeld.',
    BILL_NOT_INVOICE_OR_RECEIPT: 'Alleen facturen en bonnen kunnen aan een vast actief worden gekoppeld.',
    BILL_IS_CREDIT_NOTE: 'Een creditnota kan niet aan een vast actief worden gekoppeld.',
    BILL_NOT_IN_EURO: 'Alleen facturen in euro kunnen aan een vast actief worden gekoppeld.',
    BILL_HAS_TRANSITORY_LINE: 'Deze factuur heeft een transitorische regel. Haal die eerst weg voordat je een vast actief koppelt.',
    BILL_HAS_NO_LINE_ITEMS: 'Deze factuur heeft geen regels om op de activarekening te boeken.',
    DOCUMENT_HAS_NO_BILL: 'Dit document heeft nog geen factuurgegevens om aan een vast actief te koppelen.',
    FIXED_ASSET_ACCOUNT_NOT_ALLOWED: 'De gekozen grootboekrekening kan voor deze klant niet bij een vast actief worden gebruikt.',
    INVALID_FIXED_ASSET: 'De gegevens van het vaste actief kloppen niet. Controleer de invoer en probeer het opnieuw.',
    FIXED_ASSET_ALREADY_LINKED: 'Dit vaste actief is al aan een andere factuur gekoppeld.',
    FIXED_ASSET_NOT_LINKED: 'Aan deze factuur is geen vast actief gekoppeld.',
    SUBLEDGER_NOT_FOR_BUSINESS: 'De gekozen subadministratie hoort niet bij deze klant.',
    DOCUMENT_WAS_CHANGED: DOCUMENT_WAS_CHANGED_MESSAGE,
    BILL_ALREADY_HAS_FIXED_ASSET: 'Aan deze factuur is al een vast actief gekoppeld.',
};

function isFixedAssetErrorReason(reason: string): reason is FixedAssetErrorReason {
    return Object.prototype.hasOwnProperty.call(FIXED_ASSET_ERROR_MESSAGES, reason);
}

export function fixedAssetLinkErrorMessage(error: unknown): string {
    const conflict = documentConflictMessage(error);
    if (conflict) {
        return conflict;
    }
    const reason = errorReason(error);
    return reason && isFixedAssetErrorReason(reason) ? FIXED_ASSET_ERROR_MESSAGES[reason] : GENERIC_ERROR_MESSAGE;
}

export function isDocumentWasChanged(error: unknown): boolean {
    return hasErrorReason(error, 'DOCUMENT_WAS_CHANGED');
}

export function documentConflictMessage(error: unknown): string | null {
    if (hasErrorReason(error, 'DOCUMENT_WAS_CHANGED')) {
        return DOCUMENT_WAS_CHANGED_MESSAGE;
    }
    if (hasErrorReason(error, 'DOCUMENT_IS_BEING_PROCESSED')) {
        return 'Dit document wordt op dit moment verwerkt. Wacht tot de verwerking klaar is en probeer het daarna opnieuw.';
    }
    return null;
}

export function billConversionRefusalMessage(error: unknown): string | null {
    if (hasErrorReason(error, 'BILL_IS_MATCHED')) {
        return 'Deze factuur is al gematcht met een transactie en kan niet meer worden omgerekend.';
    }
    return null;
}

export function vatDeclarationApprovalRequestMessage(error: unknown): string | null {
    if (hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_ALREADY_REQUESTED')) {
        return 'Deze aangifte is intussen al verstuurd of goedgekeurd.';
    }
    if (hasErrorReason(error, 'VAT_DECLARATION_SNAPSHOT_NOT_LATEST_VERSION')) {
        return 'Er is intussen een nieuwere versie van deze periode opgeslagen. Verstuur die versie.';
    }
    if (hasErrorReason(error, 'VAT_DECLARATION_SNAPSHOT_CHANGED_MEANWHILE')) {
        return 'De aangifte is op hetzelfde moment gewijzigd of verstuurd. De status is bijgewerkt.';
    }
    if (hasErrorReason(error, 'NO_ENTREPRENEUR_TO_ASK_FOR_APPROVAL')) {
        return 'Deze klant heeft nog geen gebruiker die de aangifte kan goedkeuren. Nodig eerst een ondernemer uit.';
    }
    if (hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_EMAIL_NOT_DELIVERED')) {
        return 'De e-mail aan de klant kon niet worden verstuurd. De aangifte is niet verstuurd, probeer het later opnieuw.';
    }
    return null;
}

export function vatDeclarationAnswerMessage(error: unknown): string | null {
    if (hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_REQUEST_ALREADY_ANSWERED')) {
        return 'Deze aangifte is al beantwoord.';
    }
    if (hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_REQUEST_SUPERSEDED')) {
        return 'Je boekhouder heeft intussen een nieuwere versie gemaakt. Je ontvangt een nieuw verzoek zodra die klaarstaat.';
    }
    if (hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_REQUEST_WITHDRAWN')) {
        return 'Je boekhouder heeft dit verzoek ingetrokken. Het is niet meer geldig.';
    }
    if (hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_REQUEST_CHANGED_MEANWHILE')) {
        return 'Deze aangifte is op hetzelfde moment beantwoord, vervangen of ingetrokken. Bekijk de status hieronder.';
    }
    if (hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_REQUEST_ANSWERED_BY_REQUESTER')) {
        return 'Je kunt een aangifte die je zelf hebt verstuurd niet goedkeuren of afwijzen.';
    }
    return null;
}

export function vatDeclarationApprovalWithdrawMessage(error: unknown): string | null {
    if (
        hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_REQUEST_NOT_PENDING')
        || hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_REQUEST_CHANGED_MEANWHILE')
    ) {
        return 'Het verzoek is intussen al beantwoord of ingetrokken. De status is bijgewerkt.';
    }
    if (hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_REQUEST_NOT_FOUND')) {
        return 'Dit verzoek bestaat niet meer. De status is bijgewerkt.';
    }
    return null;
}

export function vatDeclarationSnapshotSaveMessage(error: unknown): string | null {
    if (hasErrorReason(error, 'VAT_DECLARATION_LOCKED_BY_APPROVAL')) {
        return 'Deze periode wacht intussen op goedkeuring van de klant of is al goedgekeurd. De status is bijgewerkt.';
    }
    if (hasErrorReason(error, 'VAT_DECLARATION_PERIOD_NOT_ENDED')) {
        return 'Deze periode is nog niet afgelopen. Je kunt de BTW-aangifte opslaan zodra de periode voorbij is.';
    }
    return null;
}

export function isVatDeclarationApprovalRequestNotFound(error: unknown): boolean {
    return hasErrorReason(error, 'VAT_DECLARATION_APPROVAL_REQUEST_NOT_FOUND');
}

export function bankAccountRenewalMessage(error: unknown): string | null {
    if (hasErrorReason(error, 'BANK_ACCOUNT_NOT_IN_CONSENT')) {
        return 'De rekening die je wilde vernieuwen zat niet bij de rekeningen die je bij je bank hebt gedeeld. Ga terug naar je bankrekeningen, klik opnieuw op Vernieuwen en selecteer bij je bank deze rekening. | CODE: ERR53490';
    }
    return null;
}
